import { useRef, type ElementType, type ReactNode } from "react";
import { cn } from "../../lib/cn";

interface SpotlightCardProps {
  as?: ElementType;
  className?: string;
  /** Color of the radial spotlight that follows the cursor. */
  spotlightColor?: string;
  padded?: boolean;
  children?: ReactNode;
  [key: string]: any;
}

/**
 * Frosted-glass adaptation of React Bits' SpotlightCard. The cursor-tracking
 * radial spotlight is preserved, but the opaque dark surface is replaced with
 * the project's Liquid Glass treatment (see `.card-spotlight` in index.css) so
 * it sits naturally alongside <GlassCard />.
 */
export function SpotlightCard({
  as: As = "div",
  className = "",
  spotlightColor = "rgba(0, 86, 255, 0.25)",
  padded = true,
  children,
  ...rest
}: SpotlightCardProps) {
  const divRef = useRef<HTMLElement | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const el = divRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
    el.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    el.style.setProperty("--spotlight-color", spotlightColor);
  };

  return (
    <As
      ref={divRef}
      onMouseMove={handleMouseMove}
      className={cn("card-spotlight", padded && "p-5", className)}
      {...rest}
    >
      {/* Top inner highlight (Apple glass hallmark), matches <GlassCard /> */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"
      />
      {children}
    </As>
  );
}

export default SpotlightCard;
